<template>
    <BaseSidebar
        :is-open="sidebarOpen"
        @close="closeBenefitDetails"
        custom-classes="max-w-[540px]"
    >
        <template v-if="selectedBenefit" #header-left>
            <div class="flex items-center">
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700"
                    :class="{ invisible: isFirstBenefit }"
                    aria-label="Previous benefit"
                    @click="navigateToPreviousBenefit"
                    :disabled="isFirstBenefit"
                >
                    <Icon icon="chevron-up" :width="20" :height="20" />
                </button>
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 ml-2"
                    :class="{ invisible: isLastBenefit }"
                    aria-label="Next benefit"
                    @click="navigateToNextBenefit"
                    :disabled="isLastBenefit"
                >
                    <Icon icon="chevron-down" :width="20" :height="20" />
                </button>
                <div
                    class="flex pl-2 gap-2 justify-start items-start text-slate-400 text-sm font-normal leading-5"
                >
                    {{ currentBenefitPosition }} of
                    {{ totalBenefitsCount }} benefits
                </div>
            </div>
        </template>
        <template #title>
            <span v-if="selectedBenefit">
                {{
                    selectedBenefit?.benefit?.benefit_title || 'Benefit Details'
                }}
            </span>
        </template>
        <!-- MY1-3084: the action menu moves up here — with only "Hide benefit"
             in it, it is not the panel's main action. -->
        <template #header-right>
            <div class="flex items-center gap-1">
                <ButtonPopover
                    v-if="selectedBenefit"
                    placement="bottom-end"
                    buttonClass="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-800 dark:hover:text-slate-100"
                    popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-sm min-w-[180px] z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700 p-2"
                >
                    <template #button-content>
                        <Icon
                            icon="dots-vertical"
                            :width="20"
                            :height="20"
                            class="rotate-90"
                            :aria-label="'Benefit actions'"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <button
                            type="button"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded text-left text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                            :disabled="visibilitySaving"
                            @click="
                                closePopover();
                                toggleBenefitVisibility();
                            "
                        >
                            <Icon
                                :icon="isBenefitHidden ? 'eye-on' : 'eye-off'"
                                :width="20"
                                :height="20"
                            />
                            <span>
                                {{ isBenefitHidden ? 'Show Benefit' : 'Hide Benefit' }}
                            </span>
                        </button>
                    </template>
                </ButtonPopover>
                <button
                    type="button"
                    class="p-2 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors duration-150"
                    aria-label="Close panel"
                    @click="closeBenefitDetails"
                >
                    <Icon icon="close" :width="24" :height="24" class="h-6 w-6" />
                </button>
            </div>
        </template>
        <div v-if="selectedBenefit" class="px-6 pb-8">
            <!-- Benefit Header (MY1-3084: centred; the action menu sits in
                 the panel header) -->
            <BenefitDetailsHeader
                :benefit="selectedBenefit"
                :is-hidden="isBenefitHidden"
                :show-last-four="showLastFour"
                :show-owner="showOwner"
                :cdn-url="cdnUrl"
                :can-enable-spending="viewerOwnsBenefitCard"
            />

            <!-- MY1-3082: connectable but unconnected card → connect-bank banner
                 (per-institution dismissal, shared with the wallet's banner) -->
            <BenefitConnectBankBanner
                :auto-tracking="selectedBenefit?.auto_tracking"
            />

            <!-- MY1-3083: connected card, owner has My Spending off → opt in
                 here on the viewer's own card, or say why it isn't tracked -->
            <BenefitSpendingPrompt
                :auto-tracking="selectedBenefit?.auto_tracking"
                :can-enable-spending="viewerOwnsBenefitCard"
                :owner-name="selectedBenefit?.user?.business_owner || selectedBenefit?.user?.name"
            />

            <!-- Usage Stats -->
            <BenefitUsageStats
                :selected-benefit="selectedBenefit"
                :is-count-based="isCountBased"
                :is-override-month="isOverrideMonth"
                :normal-monthly-value="normalMonthlyValue"
                :display-amount="displayAmount"
                :current-period-usage="currentPeriodUsage"
                :utilization-percentage="utilizationPercentage"
                :period-type-noun="periodTypeNoun"
                :current-period-label="getCurrentPeriodLabel()"
            />

            <!-- Current Period -->
            <BenefitCurrentPeriod
                ref="currentPeriodRef"
                :selected-benefit="selectedBenefit"
                :is-count-based="isCountBased"
                :is-by-count-value-benefit="isByCountValueBenefit"
                :is-mobile="isMobile"
                :display-amount="displayAmount"
                :current-period-usage="currentPeriodUsage"
                :utilization-percentage="utilizationPercentage"
                :show-fully-redeemed-icon="showFullyRedeemedIcon"
                :show-ineligible-message="showIneligibleMessage"
                :is-add-disabled="isAddDisabled"
                :is-benefit-fully-redeemed-this-year="
                    isBenefitFullyRedeemedThisYear
                "
                :expiration-time="expirationTime"
                :formatted-period-end-date="
                    store.formatYear(selectedBenefit?.period_end_date)
                "
                :renewal-tooltip-text="renewalTooltipText"
                :formatted-renewal-label="formattedRenewalLabel"
                :remaining-amount="remainingAmount"
                :remaining-count="remainingCount"
                :count-label="countLabel"
                :form-errors="formErrors"
                v-model:usage-form-data="usageFormData"
                @mobile-add-usage="handleMobileAddUsage"
                @popover-opened="closeOtherPopover"
                @usage-submit="handleUsageSubmit"
                @edit-renewal-date="openEditRenewalDateModal"
                :show-spending-option="isAutoTracked"
                @add-from-spending="openTransactionPicker"
            />

            <!-- Description -->
            <div class="mb-12 px-2.5">
                <div class="flex items-center justify-between mb-3">
                    <h4 class="font-medium text-slate-800 dark:text-slate-100">Description</h4>
                    <div
                        v-if="selectedBenefit?.benefit?.enrollment_required"
                        class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg"
                    >
                        <p class="text-slate-500 dark:text-slate-400 font-medium text-sm">
                            Enrollment required
                        </p>
                    </div>
                </div>
                <p class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {{ selectedBenefit?.benefit?.benefit_description }}
                </p>
            </div>

            <!-- Redemption History Section -->
            <div class="px-2.5">
                <div
                    v-if="!historyGroups.length"
                    class="flex items-center justify-between mb-4"
                >
                    <h4 class="font-medium text-slate-800 dark:text-slate-100">
                        Redemption history
                    </h4>
                    <!-- Only show Add usage button when there are no redemptions -->
                    <template v-if="!historyGroups.length">
                        <!-- Mobile: Always show button, use modal for adding usage -->
                        <template v-if="isMobile">
                            <button
                                class="btn-base btn-transparent !text-slate-600 !mt-0 !pr-0 disabled:!bg-white"
                                :class="{
                                    'opacity-50 cursor-not-allowed':
                                        isBenefitFullyRedeemedThisYear ||
                                        selectedBenefit?.benefit
                                            ?.show_first_year_notice,
                                }"
                                :disabled="
                                    isBenefitFullyRedeemedThisYear ||
                                    selectedBenefit?.benefit
                                        ?.show_first_year_notice
                                "
                                @click.stop="handleMobileAddUsage"
                            >
                                <Icon icon="plus" :width="16" :height="16" />
                                Add usage
                            </button>
                        </template>

                        <!-- Desktop: Use tooltip for disabled state, popover for active state -->
                        <template v-else>
                            <button
                                v-if="
                                    isBenefitFullyRedeemedThisYear ||
                                    selectedBenefit?.benefit
                                        ?.show_first_year_notice
                                "
                                class="btn-base btn-transparent !text-slate-600 !mt-0 !pr-0 opacity-50 cursor-not-allowed !bg-white"
                                disabled
                            >
                                <Icon icon="plus" :width="16" :height="16" />
                                Add usage
                            </button>
                            <ButtonPopover
                                v-else
                                ref="redemptionHistoryPopover"
                                @click.stop="closeOtherPopover('redemption')"
                                buttonClass="btn-base btn-transparent !text-slate-600 !mt-0 !pr-0"
                                popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-80 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700"
                            >
                                <template #button-content>
                                    <span
                                        class="inline-flex items-center gap-2"
                                        @click="addUsageMode = 'menu'"
                                    >
                                        <Icon
                                            icon="plus"
                                            :width="16"
                                            :height="16"
                                        />
                                        Add usage
                                    </span>
                                </template>
                                <template #default="{ closePopover }">
                                    <!-- MY1-3080: an auto-tracked benefit first chooses between
                                         a My Spending transaction and manual entry -->
                                    <AddUsageMenu
                                        v-if="isAutoTracked && addUsageMode === 'menu'"
                                        @spending="
                                            closePopover();
                                            openTransactionPicker();
                                        "
                                        @manual="addUsageMode = 'manual'"
                                    />
                                    <div v-else class="p-4">
                                        <BenefitsUsageForm
                                            v-model="usageFormData"
                                            :hideLabels="true"
                                            :errors="formErrors"
                                            :isCountBased="isCountBased"
                                            :countLabel="countLabel"
                                        />
                                        <div
                                            class="flex justify-between items-center mt-4"
                                        >
                                            <!-- Remaining Display -->
                                            <div
                                                class="text-slate-500 dark:text-slate-400 text-sm"
                                                v-if="
                                                    (isCountBased ||
                                                        isByCountValueBenefit) &&
                                                    remainingCount > 0
                                                "
                                            >
                                                {{ remainingCount }}
                                                remaining
                                            </div>
                                            <div
                                                class="text-slate-500 dark:text-slate-400 text-sm"
                                                v-else-if="
                                                    !isCountBased &&
                                                    !isByCountValueBenefit &&
                                                    parseFloat(
                                                        remainingAmount
                                                    ) > 0
                                                "
                                            >
                                                {{
                                                    filters.formatSmartMoney(
                                                        remainingAmount
                                                    )
                                                }}
                                                remaining
                                            </div>
                                            <div
                                                v-else
                                                class="text-green-600 text-sm font-medium"
                                            >
                                                Fully redeemed!
                                            </div>

                                            <!-- Submit Button -->
                                            <button
                                                @click="
                                                    handleUsageSubmit(
                                                        closePopover
                                                    )
                                                "
                                                class="btn-base btn-dark"
                                                :disabled="
                                                    isCountBased ||
                                                    isByCountValueBenefit
                                                        ? remainingCount <= 0
                                                        : !parseFloat(
                                                              remainingAmount
                                                          )
                                                "
                                            >
                                                <svg
                                                    class="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span>Redeem</span>
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </ButtonPopover>
                        </template>
                    </template>
                </div>
                <BenefitRedeemingHistoryChart
                    v-if="selectedBenefit && historyGroups.length"
                    :benefit="selectedBenefit"
                    class="mb-6"
                />
                <!-- Empty state lives outside the collapse so the gradient
                     and Show-all button never appear with zero records. -->
                <p v-if="!historyGroups.length" class="text-sm text-slate-500 dark:text-slate-400">
                    No redemptions recorded. Record usage to see benefit
                    history.
                </p>
                <div v-else class="relative">
                    <div
                        ref="historyListRef"
                        :class="[
                            'relative transition-[max-height] duration-300 ease-in-out',
                            historyExpanded
                                ? 'max-h-none'
                                : 'max-h-[320px] overflow-hidden',
                        ]"
                    >
                        <div v-show="historyGroups.length">
                            <div
                                v-for="group in historyGroups"
                                :key="group.periodKey"
                                class="mb-4"
                            >
                                <!-- Group Header -->
                                <div
                                    class="flex items-center justify-between pb-2 sm:border-b border-slate-200 dark:border-slate-700 text-sm sm:font-medium font-normal text-slate-800 dark:text-slate-100"
                                >
                                    <div class="flex items-center gap-2">
                                        <span>
                                            {{ labelFor(group.periodKey) }}
                                        </span>
                                    </div>
                                    <div>
                                        <template v-if="!isCountBased">
                                            {{
                                                filters.formatMoney(
                                                    group.totalAmount
                                                )
                                            }}
                                            of
                                            {{
                                                filters.formatMoney(
                                                    group.annualValue
                                                )
                                            }}
                                            used
                                        </template>
                                        <template v-else>
                                            {{ group.totalAmount || 0 }}
                                            of
                                            {{
                                                selectedBenefit?.benefit
                                                    ?.value_details?.count || 0
                                            }}
                                            used
                                        </template>
                                    </div>
                                </div>

                                <!-- Usage Entries -->
                                <div>
                                    <div
                                        v-for="entry in group.entries"
                                        :key="entry.uuid || entry.id"
                                        class="group flex items-center justify-between py-1.5 my-1 text-sm sm:hover:bg-slate-100 dark:sm:hover:bg-slate-700 rounded cursor-pointer text-slate-800 dark:text-slate-100 font-normal relative"
                                    >
                                        <div>
                                            <p class="sm:hidden inline">
                                                <template v-if="!isCountBased">
                                                    {{
                                                        filters.formatMoney(
                                                            entry.total_amount_used
                                                        )
                                                    }}
                                                </template>
                                                <template v-else>
                                                    {{ entry.usage_count || 0 }}
                                                </template>
                                            </p>
                                            <div
                                                class="max-sm:text-slate-500 dark:max-sm:text-slate-400 flex items-baseline gap-1.5"
                                            >
                                                {{ entry.formattedDate }}
                                                <!-- MY1-3085: note preview — first four lines, then truncated -->
                                                <UiTooltip
                                                    v-if="entry.notes"
                                                    placement="top"
                                                    :interactive="false"
                                                >
                                                    <template #trigger>
                                                        <span
                                                            class="inline-flex items-center cursor-default"
                                                            :aria-label="`Note: ${entry.notes}`"
                                                            data-test="usage-note-icon"
                                                        >
                                                            <Icon
                                                                icon="note"
                                                                :width="14"
                                                                :height="14"
                                                                class="text-slate-400"
                                                            />
                                                        </span>
                                                    </template>
                                                    <span
                                                        class="block text-left whitespace-pre-line break-words line-clamp-4"
                                                        data-test="usage-note-preview"
                                                    >{{ entry.notes }}</span>
                                                </UiTooltip>
                                            </div>
                                            <!-- MY1-3079: the My Spending transaction behind the record -->
                                            <BenefitUsageDataLink :entry="entry" class="mt-0.5" />
                                        </div>
                                        <ButtonPopover
                                            buttonClass="sm:!hidden flex items-center justify-center btn-icon btn-transparent !text-slate-600 !mt-0 -mr-2"
                                            popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700 min-w-32"
                                        >
                                            <template #button-content>
                                                <Icon
                                                    icon="dots-vertical"
                                                    class="w-5 h-5 rotate-90"
                                                />
                                            </template>
                                            <template
                                                #default="{ closePopover }"
                                            >
                                                <div
                                                    class="flex flex-col justify-start"
                                                >
                                                    <button
                                                        class="btn-base btn-transparent !m-0 !font-normal !justify-start"
                                                        @click="
                                                            openEditUsageModal(
                                                                entry
                                                            )
                                                        "
                                                    >
                                                        <Icon icon="pencil" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        class="btn-base btn-transparent !m-0 !font-normal !justify-start"
                                                        @click="
                                                            openDeleteModal(
                                                                entry
                                                            )
                                                        "
                                                    >
                                                        <Icon icon="trash" />
                                                        Delete
                                                    </button>
                                                </div>
                                            </template>
                                        </ButtonPopover>
                                        <div
                                            class="items-center gap-2 sm:flex hidden"
                                        >
                                            <div
                                                class="opacity-0 group-hover:opacity-100 transition-opacity items-center gap-1 flex"
                                            >
                                                <button
                                                    @click.stop="
                                                        openEditUsageModal(
                                                            entry
                                                        )
                                                    "
                                                    class="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                                                    title="Edit usage"
                                                >
                                                    <Icon
                                                        icon="pencil"
                                                        class="w-4 h-4 text-slate-500"
                                                    />
                                                </button>
                                                <!-- MY1-3081: "Tooltip for 'Delete' Button" -->
                                                <UiTooltip
                                                    text="Delete history record"
                                                    placement="top"
                                                    :interactive="false"
                                                >
                                                    <template #trigger>
                                                        <button
                                                            @click.stop="
                                                                openDeleteModal(entry)
                                                            "
                                                            class="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                                                            aria-label="Delete history record"
                                                        >
                                                            <Icon
                                                                icon="trash"
                                                                class="w-4 h-4 text-slate-500"
                                                            />
                                                        </button>
                                                    </template>
                                                </UiTooltip>
                                            </div>
                                            <div>
                                                <template v-if="!isCountBased">
                                                    {{
                                                        filters.formatMoney(
                                                            entry.total_amount_used
                                                        )
                                                    }}
                                                </template>
                                                <template v-else>
                                                    {{ entry.usage_count || 0 }}
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- White gradient fade hint when collapsed and overflowing. -->
                    <div
                        v-if="!historyExpanded && historyOverflowing"
                        class="pointer-events-none absolute bottom-0 inset-x-0 h-12 bg-gradient-to-b from-transparent to-white dark:to-slate-800"
                    />
                </div>
                <button
                    v-if="historyOverflowing"
                    type="button"
                    class="mt-4 text-slate-800 dark:text-slate-100 hover:text-slate-500 dark:hover:text-slate-300 text-sm font-medium"
                    @click="historyExpanded = !historyExpanded"
                >
                    {{ historyExpanded ? 'Show less' : 'Show all' }}
                </button>
            </div>
        </div>
    </BaseSidebar>

    <!-- Edit Usage Modal -->
    <EditUsageModalMobile
        v-if="isMobile"
        :show-edit-usage-modal="showEditUsageModal"
        :selected-benefit="selectedBenefit"
        :initial-data="{
            amount: editingEntry?.total_amount_used,
            date: editingEntry?.used_at,
            uuid: editingEntry?.uuid,
        }"
        @close="closeEditUsageModal"
        @save="handleUsageUpdated"
    />
    <EditUsageModal
        v-else
        :show-edit-usage-modal="showEditUsageModal"
        v-model="usageFormData"
        :editing-entry="editingEntry"
        @usage-saved="handleUsageSaved"
        @close="closeEditUsageModal"
    />

    <!-- Delete Confirmation Modal (MY1-3081: auto-tracked records add the
         "What's wrong?" feedback step) -->
    <DeleteUsageRecordModal
        v-if="showDeleteModal"
        :is-open="showDeleteModal"
        :entry="entryToDelete"
        :benefit-title="selectedBenefit?.benefit?.benefit_title"
        :format-date="formatDate"
        v-model:feedback="deleteFeedback"
        @close="showDeleteModal = false"
        @confirm="confirmDelete"
    />

    <!-- Edit Renewal Date Modal -->
    <EditRenewalDateModal
        :is-open="showRenewalDateModal"
        @close="showRenewalDateModal = false"
    />
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { parseLocalDate } from '@/utils/dateUtils';
import filters from '@/filters';
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import BenefitRedeemingHistoryChart from '@/components/card-benefits/benefit-redeeming-history-chart.vue';
import Icon from '@/components/ui/icon/icon.vue';
import EditUsageModal from '@/components/card-benefits/edit-usage-modal.vue';
import EditUsageModalMobile from '@/components/card-benefits/edit-usage-modal-mobile.vue';
import EditRenewalDateModal from '@/components/card-benefits/edit-renewal-date-modal.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import BenefitUsageStats from '@/components/card-benefits/benefit-details-sidebar/BenefitUsageStats.vue';
import BenefitCurrentPeriod from '@/components/card-benefits/benefit-details-sidebar/BenefitCurrentPeriod.vue';
import BenefitUsageDataLink from '@/components/card-benefits/benefit-details-sidebar/BenefitUsageDataLink.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import AddUsageMenu from '@/components/card-benefits/add-usage-menu.vue';
import DeleteUsageRecordModal from '@/components/card-benefits/benefit-details-sidebar/DeleteUsageRecordModal.vue';
import BenefitDetailsHeader from '@/components/card-benefits/benefit-details-sidebar/BenefitDetailsHeader.vue';
import BenefitConnectBankBanner from '@/components/card-benefits/benefit-details-sidebar/BenefitConnectBankBanner.vue';
import BenefitSpendingPrompt from '@/components/card-benefits/benefit-details-sidebar/BenefitSpendingPrompt.vue';
import BenefitsUsageForm from '@/components/card-benefits/benefits-usage-form.vue';
import { useBenefitDetails } from '@/composables/useBenefitDetails';

const currentPeriodRef = ref(null);
const redemptionHistoryRef = ref(null);
const redemptionHistoryPopover = ref(null);

const {
    // Store refs
    store,
    selectedBenefit,
    sidebarOpen,
    showLastFour,
    currentBenefitPosition,
    totalBenefitsCount,
    isFirstBenefit,
    isLastBenefit,
} = useBenefitDetails();

// Show the benefit owner next to the card title only when the wallet has
// more than one tracked user — for solo wallets the name would just repeat
// across every detail panel and add visual noise.
// MY1-3080: "Add from My Spending" is offered on auto-tracked benefits; the
// Add usage popovers open on a source menu and fall through to the form.
const isAutoTracked = computed(
    () => selectedBenefit.value?.auto_tracking?.state === 'auto_tracked'
);
const addUsageMode = ref('menu');
const openTransactionPicker = () => {
    store.openTransactionPicker(selectedBenefit.value, remainingAmount.value);
};

const showOwner = computed(() => store.insightsUserOptions.length > 1);

// MY1-3083 / MY1-3077: the My Spending opt-in mutates the signed-in user, so
// it is only offered on cards the viewer owns. The payload's entity id is the
// owner's user id for personal cards; business cards carry a synthetic id, so
// they get the passive note (the owner can still opt in from the label's
// tooltip destination or the wallet).
const viewerOwnsBenefitCard = computed(() => {
    const owner = selectedBenefit.value?.user;

    if (!owner || owner.is_business) {
        return false;
    }

    const viewerId = usePage().props.userData?.id;

    return viewerId != null && Number(owner.id) === Number(viewerId);
});

const {
    // Navigation
    closeBenefitDetails,
    navigateToPreviousBenefit,
    navigateToNextBenefit,

    // Mobile
    isMobile,

    // Modal state
    showEditUsageModal,
    showDeleteModal,
    showRenewalDateModal,
    entryToDelete,
    deleteFeedback,
    editingEntry,
    formErrors,
    usageFormData,

    // Computed
    cdnUrl,
    historyGroups,
    labelFor,
    displayAmount,
    isOverrideMonth,
    normalMonthlyValue,
    isCountBased,
    isByCountValueBenefit,
    countLabel,
    remainingAmount,
    remainingCount,
    utilizationPercentage,
    showFullyRedeemedIcon,
    currentPeriodUsage,
    showIneligibleMessage,
    expirationTime,
    renewalTooltipText,
    formattedRenewalLabel,
    periodTypeNoun,
    isAddDisabled,
    isHistoryAddDisabled,
    isBenefitFullyRedeemedThisYear,
    isBenefitHidden,
    visibilitySaving,

    // Methods
    getCurrentPeriodLabel,
    formatDate,
    openEditUsageModal,
    openDeleteModal,
    handleMobileAddUsage,
    handleUsageSubmit,
    confirmDelete,
    closeEditUsageModal,
    handleUsageSaved,
    handleUsageUpdated,
    openEditRenewalDateModal,
    toggleBenefitVisibility,
} = useBenefitDetails();

// Collapsible Redeeming history list: when the rendered list exceeds the
// max height, show a "Show all" button + white gradient fade.
const historyListRef = ref(null);
const historyExpanded = ref(false);
const historyOverflowing = ref(false);

const checkHistoryOverflow = () => {
    const el = historyListRef.value;
    if (!el) {
        historyOverflowing.value = false;
        return;
    }
    historyOverflowing.value = el.scrollHeight - el.clientHeight >= 9;
};

watch(
    [historyGroups, selectedBenefit],
    () => {
        // Reset collapse so navigating between benefits via the sidebar
        // chevrons doesn't carry over a previous benefit's expanded state.
        historyExpanded.value = false;
        nextTick(checkHistoryOverflow);
    },
    { immediate: true, deep: true }
);

// Close popovers and reset form when navigating between benefits
watch(selectedBenefit, () => {
    // Close any open popovers
    redemptionHistoryPopover.value?.close?.();
    currentPeriodRef.value?.popoverRef?.close?.();
    redemptionHistoryRef.value?.popoverRef?.close?.();

    // Reset form data and errors
    usageFormData.value = {
        amount: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        count: '',
    };
    formErrors.value = {};
});

// Only re-check overflow when collapsing back. While expanded the container
// is `max-h-none`, so scrollHeight ≈ clientHeight and the check would zero
// out `historyOverflowing` — making the "Show less" button vanish and trap
// the user in the expanded state.
watch(historyExpanded, (expanded) => {
    if (!expanded) {
        nextTick(checkHistoryOverflow);
    }
});

onMounted(() => {
    nextTick(checkHistoryOverflow);
});

onUnmounted(() => {
    historyExpanded.value = false;
    nextTick(checkHistoryOverflow);
});

// Close other popover when one is opened
const closeOtherPopover = (opened) => {
    if (opened === 'current') {
        redemptionHistoryRef.value?.popoverRef?.close();
    } else if (opened === 'redemption') {
        currentPeriodRef.value?.popoverRef?.close();
    }
};
</script>
